"use client";

import { useMemo, useState } from "react";
import Link from "next/link";
import { Eye, EyeOff, Loader2, Mail } from "lucide-react";
import {
  AuthBrand,
  AuthShell,
  LegalFooter,
  authFieldClass,
  authOutlineBtnClass,
  authPrimaryBtnClass,
} from "@/components/auth/login-view";
import { TestGate } from "@/dev-testbed/ui/testlauf/TestGate.tsx";
import { VorgabeHinweis } from "@/dev-testbed/ui/testlauf/VorgabeHinweis.tsx";
import { useVorgabe } from "@/dev-testbed/ui/testlauf/use-vorgabe.ts";
import { companyWebsiteBlockReason } from "@/lib/auth/company-website";

type PendingVerification = {
  email: string;
  companyId: string | null;
  mailDelivered: boolean;
  message: string;
  mailHint: string | null;
  devVerifyUrl: string | null;
};

export function RegisterForm() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [passwordConfirm, setPasswordConfirm] = useState("");
  const [websiteUrl, setWebsiteUrl] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState<PendingVerification | null>(null);
  const [resendInfo, setResendInfo] = useState<string | null>(null);
  const [resending, setResending] = useState(false);

  const websiteV = useVorgabe("company.website", {
    value: websiteUrl,
    setValue: setWebsiteUrl,
  });
  const emailV = useVorgabe("user.email", { value: email, setValue: setEmail });
  const passwordV = useVorgabe("user.password", {
    value: password,
    setValue: setPassword,
  });
  const passwordConfirmV = useVorgabe("user.passwordConfirm", {
    value: passwordConfirm,
    setValue: setPasswordConfirm,
  });

  const websiteValid = useMemo(() => {
    const v = websiteUrl.trim();
    if (v.length < 3) return false;
    return (
      /^https?:\/\//i.test(v) ||
      (/^[a-z0-9.-]+\.[a-z]{2,}/i.test(v) && !/\s/.test(v))
    );
  }, [websiteUrl]);

  function storePreparePayload(json: {
    companyId?: string;
    website?: string | null;
  }) {
    try {
      sessionStorage.setItem(
        "strategyiq_prepare",
        JSON.stringify({
          companyId: json.companyId,
          websiteUrl: json.website ?? websiteUrl.trim(),
          companyLabel: json.website ?? websiteUrl.trim(),
        }),
      );
    } catch {
      /* ignore */
    }
  }

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setResendInfo(null);
    if (!websiteValid) {
      setError("Bitte geben Sie eine gültige Unternehmenswebsite an.");
      return;
    }
    const websiteBlock = companyWebsiteBlockReason(websiteUrl.trim());
    if (websiteBlock) {
      setError(websiteBlock);
      return;
    }
    if (password.length < 8) {
      setError("Passwort muss mindestens 8 Zeichen haben.");
      return;
    }
    if (password !== passwordConfirm) {
      setError("Passwörter stimmen nicht überein.");
      return;
    }
    setLoading(true);
    try {
      const res = await fetch("/api/auth/register", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          email,
          password,
          websiteUrl: websiteUrl.trim(),
          startOnboarding: true,
          department: "Management",
        }),
      });
      const json = (await res.json()) as {
        message?: string;
        needsVerification?: boolean;
        email?: string;
        companyId?: string;
        website?: string | null;
        mailDelivered?: boolean;
        mailHint?: string | null;
        devVerifyUrl?: string;
      };
      if (!res.ok) throw new Error(json.message ?? "Registrierung fehlgeschlagen");

      storePreparePayload(json);

      if (json.needsVerification) {
        setPending({
          email: json.email ?? email,
          companyId: typeof json.companyId === "string" ? json.companyId : null,
          mailDelivered: Boolean(json.mailDelivered),
          message:
            json.message ??
            "Bitte bestätigen Sie Ihre E-Mail-Adresse über den Link in Ihrem Postfach.",
          mailHint: json.mailHint ?? null,
          devVerifyUrl: json.devVerifyUrl ?? null,
        });
        return;
      }

      // Fallback: sollte nach dem Auth-Lifecycle nicht mehr vorkommen.
      window.location.href = "/onboarding/preparing";
    } catch (err) {
      setError(err instanceof Error ? err.message : "Fehler");
    } finally {
      setLoading(false);
    }
  }

  async function resendVerification() {
    if (!pending) return;
    setResending(true);
    setResendInfo(null);
    setError(null);
    try {
      const res = await fetch("/api/auth/resend-verification", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          email: pending.email,
          companyId: pending.companyId ?? undefined,
        }),
      });
      const json = (await res.json()) as {
        message?: string;
        mailDelivered?: boolean;
        mailHint?: string | null;
        devVerifyUrl?: string;
      };
      if (!res.ok) throw new Error(json.message ?? "Erneuter Versand fehlgeschlagen");

      setPending((prev) =>
        prev
          ? {
              ...prev,
              mailDelivered: Boolean(json.mailDelivered),
              mailHint: json.mailHint ?? prev.mailHint,
              devVerifyUrl: json.devVerifyUrl ?? prev.devVerifyUrl,
              message: json.message ?? prev.message,
            }
          : prev,
      );
      setResendInfo(
        json.message ??
          (json.mailDelivered
            ? "Neuer Bestätigungslink wurde gesendet."
            : "Zustellung fehlgeschlagen — bitte erneut versuchen."),
      );
    } catch (err) {
      setError(err instanceof Error ? err.message : "Erneuter Versand fehlgeschlagen");
    } finally {
      setResending(false);
    }
  }

  if (pending) {
    return (
      <AuthShell>
        <AuthBrand />
        <div className="space-y-3 text-center">
          <Mail className="mx-auto h-10 w-10 text-[#8b8b96]" aria-hidden />
          <p className="text-lg font-medium text-[#f3f3f5]">E-Mail bestätigen</p>
          <p className="text-sm text-[#b4b4be]">
            Wir haben einen Bestätigungslink an{" "}
            <span className="font-medium text-[#f3f3f5]">{pending.email}</span>{" "}
            gesendet. Öffnen Sie den Link, um Ihr Konto zu aktivieren. Danach
            können Sie sich anmelden — oder Sie werden über den Link direkt
            angemeldet.
          </p>
          {!pending.mailDelivered ? (
            <p className="rounded-xl border border-[#37373f] bg-[#1f1f24] px-3.5 py-3 text-left text-sm text-[#ed5d64]">
              {pending.message}
              {pending.mailHint ? ` ${pending.mailHint}` : ""}
            </p>
          ) : (
            <p className="text-sm text-[#65c985]">{pending.message}</p>
          )}
          {pending.devVerifyUrl ? (
            <p className="rounded-xl border border-[#37373f] bg-[#1f1f24] px-3.5 py-3 text-left text-[12px] text-[#8b8b96]">
              Entwicklungslink (Mail nicht zugestellt):{" "}
              <a
                href={pending.devVerifyUrl}
                className="break-all font-mono text-[#f3f3f5] underline"
              >
                {pending.devVerifyUrl}
              </a>
            </p>
          ) : null}
          {resendInfo ? (
            <p className="text-sm text-[#65c985]">{resendInfo}</p>
          ) : null}
          {error ? <p className="text-sm text-[#ed5d64]">{error}</p> : null}
          <button
            type="button"
            disabled={resending}
            onClick={() => void resendVerification()}
            className={authOutlineBtnClass}
          >
            {resending ? (
              <Loader2 className="h-5 w-5 animate-spin" />
            ) : (
              "E-Mail erneut senden"
            )}
          </button>
          <Link href="/login" className={authPrimaryBtnClass}>
            Zur Anmeldung
          </Link>
          <p className="pt-2 text-center text-sm text-[#8b8b96]">
            Falsche E-Mail?{" "}
            <button
              type="button"
              className="font-medium text-[#f3f3f5] underline"
              onClick={() => {
                setPending(null);
                setResendInfo(null);
                setError(null);
              }}
            >
              Erneut registrieren
            </button>
          </p>
          <LegalFooter />
        </div>
      </AuthShell>
    );
  }

  return (
    <AuthShell>
      <AuthBrand />
      <form onSubmit={(e) => void submit(e)} className="space-y-3">
        <p className="pb-1 text-center text-sm text-[#8b8b96]">
          Unternehmen registrieren
        </p>
        <div>
          <input
            type="email"
            required
            autoComplete="email"
            placeholder="Geschäftliche E-Mail-Adresse"
            value={email}
            onChange={(e) => emailV.onChangeWert(e.target.value, setEmail)}
            className={authFieldClass}
            autoFocus
            {...emailV.inputProps}
          />
          {emailV.aktiv && emailV.sperre ? (
            <VorgabeHinweis
              id={emailV.hinweisId}
              sperre={emailV.sperre}
              hinweis={emailV.hinweis}
            />
          ) : null}
        </div>
        <div>
          <input
            type="url"
            required
            placeholder="Unternehmenswebsite (z. B. firma.de)"
            value={websiteUrl}
            onChange={(e) => websiteV.onChangeWert(e.target.value, setWebsiteUrl)}
            className={authFieldClass}
            {...websiteV.inputProps}
          />
          {websiteV.aktiv && websiteV.sperre ? (
            <VorgabeHinweis
              id={websiteV.hinweisId}
              sperre={websiteV.sperre}
              hinweis={websiteV.hinweis}
            />
          ) : null}
        </div>
        <div>
          <div className="relative">
            <input
              type={showPassword ? "text" : "password"}
              required
              minLength={8}
              autoComplete="new-password"
              placeholder="Passwort (min. 8 Zeichen)"
              value={password}
              onChange={(e) => passwordV.onChangeWert(e.target.value, setPassword)}
              className={`${authFieldClass} pr-11`}
              {...passwordV.inputProps}
            />
            <button
              type="button"
              onClick={() => setShowPassword((v) => !v)}
              className="absolute right-3 top-1/2 -translate-y-1/2 rounded-md p-1 text-[#8b8b96] hover:text-[#f3f3f5]"
            >
              {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </button>
          </div>
          {passwordV.aktiv && passwordV.sperre ? (
            <VorgabeHinweis
              id={passwordV.hinweisId}
              sperre={passwordV.sperre}
              hinweis={passwordV.hinweis}
            />
          ) : null}
        </div>
        <div>
          <input
            type={showPassword ? "text" : "password"}
            required
            minLength={8}
            autoComplete="new-password"
            placeholder="Passwort bestätigen"
            value={passwordConfirm}
            onChange={(e) =>
              passwordConfirmV.onChangeWert(e.target.value, setPasswordConfirm)
            }
            className={authFieldClass}
            {...passwordConfirmV.inputProps}
          />
          {passwordConfirmV.aktiv && passwordConfirmV.sperre ? (
            <VorgabeHinweis
              id={passwordConfirmV.hinweisId}
              sperre={passwordConfirmV.sperre}
              hinweis={passwordConfirmV.hinweis}
            />
          ) : null}
        </div>
        {error && <p className="text-sm text-[#ed5d64]">{error}</p>}
        <TestGate aktion="auth.register.submit">
          <button
            type="submit"
            disabled={loading}
            className={authPrimaryBtnClass}
            data-test-aktion="auth.register.submit"
          >
            {loading ? (
              <Loader2 className="h-5 w-5 animate-spin" />
            ) : (
              "Registrieren"
            )}
          </button>
        </TestGate>
        <p className="pt-2 text-center text-sm text-[#8b8b96]">
          Bereits registriert?{" "}
          <Link href="/login" className="font-medium text-[#f3f3f5] underline">
            Anmelden
          </Link>
        </p>
        <LegalFooter />
      </form>
    </AuthShell>
  );
}
